// Licensed to the Apache Software Foundation (ASF) under one
// or more contributor license agreements.  See the NOTICE file
// distributed with this work for additional information
// regarding copyright ownership.  The ASF licenses this file
// to you under the Apache License, Version 2.0 (the
// "License"); you may not use this file except in compliance
// with the License.  You may obtain a copy of the License at
//
//   http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing,
// software distributed under the License is distributed on an
// "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
// KIND, either express or implied.  See the License for the
// specific language governing permissions and limitations
// under the License.

<template>
  <a-spin :spinning="loading">
    <div v-if="!mapping" class="empty-state">
      <a-empty :description="$t('label.oauth2.mapping.none')">
        <a-button v-if="isAdmin" type="primary" @click="openAddDialog">
          <template #icon><plus-outlined /></template>
          {{ $t('label.oauth2.mapping.add') }}
        </a-button>
      </a-empty>
    </div>

    <div v-else>
      <a-descriptions
        :title="$t('label.oauth2.mapping.title')"
        bordered
        :column="1"
        size="small">
        <a-descriptions-item :label="$t('label.domain')">
          {{ mapping.domainname }}
        </a-descriptions-item>
        <a-descriptions-item :label="$t('label.oauth2.realmurl')">
          <a :href="mapping.realmurl" target="_blank" rel="noopener">{{ mapping.realmurl }}</a>
        </a-descriptions-item>
        <a-descriptions-item :label="$t('label.oauth2.clientid')">
          {{ mapping.clientid }}
        </a-descriptions-item>
        <a-descriptions-item :label="$t('label.oauth2.clientsecret')">
          <a-tag>{{ $t('label.oauth2.clientsecret.hidden') }}</a-tag>
        </a-descriptions-item>
        <a-descriptions-item :label="$t('label.oauth2.redirecturi')">
          {{ mapping.redirecturi }}
        </a-descriptions-item>
        <a-descriptions-item :label="$t('label.enabled')">
          <a-tag :color="mapping.enabled ? 'green' : 'red'">
            {{ mapping.enabled ? $t('label.yes') : $t('label.no') }}
          </a-tag>
        </a-descriptions-item>
        <a-descriptions-item :label="$t('label.created')">
          {{ $toLocaleDate(mapping.created) }}
        </a-descriptions-item>
      </a-descriptions>

      <div v-if="isAdmin" class="actions">
        <a-button @click="openEditDialog">
          <template #icon><edit-outlined /></template>
          {{ $t('label.edit') }}
        </a-button>
        <a-popconfirm
          :title="$t('label.oauth2.mapping.remove.confirm')"
          :ok-text="$t('label.yes')"
          :cancel-text="$t('label.no')"
          @confirm="handleRemove">
          <a-button danger>
            <template #icon><delete-outlined /></template>
            {{ $t('label.oauth2.mapping.remove') }}
          </a-button>
        </a-popconfirm>
      </div>
    </div>

    <a-modal
      :visible="dialogVisible"
      :title="dialogMode === 'add' ? $t('label.oauth2.mapping.add') : $t('label.oauth2.mapping.edit')"
      :confirm-loading="submitting"
      :mask-closable="false"
      @ok="handleSubmit"
      @cancel="closeDialog">
      <a-form
        :ref="formRef"
        :model="form"
        :rules="rules"
        layout="vertical">
        <a-form-item name="realmUrl" :label="$t('label.oauth2.realmurl')">
          <a-input
            v-model:value="form.realmUrl"
            :placeholder="$t('label.oauth2.realmurl.placeholder')" />
        </a-form-item>
        <a-form-item name="clientId" :label="$t('label.oauth2.clientid')">
          <a-input v-model:value="form.clientId" />
        </a-form-item>
        <a-form-item name="clientSecret" :label="$t('label.oauth2.clientsecret')">
          <a-input-password
            v-model:value="form.clientSecret"
            :placeholder="dialogMode === 'edit' ? $t('label.oauth2.clientsecret.keep') : ''" />
        </a-form-item>
        <a-form-item name="redirectUri" :label="$t('label.oauth2.redirecturi')">
          <a-input
            v-model:value="form.redirectUri"
            :placeholder="$t('label.oauth2.redirecturi.placeholder')" />
        </a-form-item>
        <a-form-item name="enabled" :label="$t('label.enabled')">
          <a-switch v-model:checked="form.enabled" />
        </a-form-item>
      </a-form>
    </a-modal>
  </a-spin>
</template>

<script>
import { ref, reactive } from 'vue'
import { getAPI, postAPI } from '@/api'

export default {
  name: 'OAuth2DomainMappingTab',
  props: {
    resource: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      loading: false,
      submitting: false,
      mapping: null,
      dialogVisible: false,
      dialogMode: 'add'
    }
  },
  computed: {
    isAdmin () {
      return this.$store.getters.userInfo?.roletype === 'Admin'
    }
  },
  created () {
    this.initForm()
    this.fetchMapping()
  },
  watch: {
    'resource.id' () {
      this.fetchMapping()
    }
  },
  methods: {
    initForm () {
      this.formRef = ref()
      this.form = reactive({
        realmUrl: '',
        clientId: '',
        clientSecret: '',
        redirectUri: '',
        enabled: true
      })
      this.rules = reactive({
        realmUrl: [{ required: true, message: this.$t('label.required') }],
        clientId: [{ required: true, message: this.$t('label.required') }],
        redirectUri: [{ required: true, message: this.$t('label.required') }]
      })
    },
    fetchMapping () {
      if (!this.resource?.id) return
      this.loading = true
      this.mapping = null
      getAPI('listOAuth2DomainProviderMappings', { domainid: this.resource.id }).then(response => {
        const list = response?.listoauth2domainprovidermappingsresponse?.oauth2domainprovidermapping || []
        this.mapping = list.length > 0 ? list[0] : null
      }).catch(error => {
        this.$notification.error({
          message: this.$t('message.request.failed'),
          description: this.errorMessage(error),
          duration: 0
        })
      }).finally(() => {
        this.loading = false
      })
    },
    openAddDialog () {
      this.dialogMode = 'add'
      this.form.realmUrl = ''
      this.form.clientId = ''
      this.form.clientSecret = ''
      this.form.redirectUri = ''
      this.form.enabled = true
      this.rules.clientSecret = [{ required: true, message: this.$t('label.required') }]
      this.dialogVisible = true
    },
    openEditDialog () {
      this.dialogMode = 'edit'
      this.form.realmUrl = this.mapping.realmurl
      this.form.clientId = this.mapping.clientid
      this.form.clientSecret = ''
      this.form.redirectUri = this.mapping.redirecturi
      this.form.enabled = !!this.mapping.enabled
      this.rules.clientSecret = []
      this.dialogVisible = true
    },
    closeDialog () {
      if (this.submitting) return
      this.dialogVisible = false
    },
    handleSubmit () {
      this.formRef.value.validate().then(() => {
        if (this.dialogMode === 'add') {
          this.submitAdd()
        } else {
          this.submitUpdate()
        }
      }).catch(error => {
        if (error?.errorFields?.length) {
          this.formRef.value.scrollToField(error.errorFields[0].name)
        }
      })
    },
    submitAdd () {
      this.submitting = true
      postAPI('addOAuth2DomainProviderMapping', {
        domainid: this.resource.id,
        realmurl: this.form.realmUrl,
        clientid: this.form.clientId,
        clientsecret: this.form.clientSecret,
        redirecturi: this.form.redirectUri,
        enabled: this.form.enabled
      }).then(() => {
        this.$notification.success({
          message: this.$t('label.oauth2.mapping.add.success')
        })
        this.dialogVisible = false
        this.fetchMapping()
      }).catch(error => {
        this.$notification.error({
          message: this.$t('message.request.failed'),
          description: this.errorMessage(error),
          duration: 0
        })
      }).finally(() => {
        this.submitting = false
      })
    },
    submitUpdate () {
      this.submitting = true
      const params = {
        domainid: this.resource.id,
        realmurl: this.form.realmUrl,
        clientid: this.form.clientId,
        redirecturi: this.form.redirectUri,
        enabled: this.form.enabled
      }
      if (this.form.clientSecret) {
        params.clientsecret = this.form.clientSecret
      }
      postAPI('updateOAuth2DomainProviderMapping', params).then(() => {
        this.$notification.success({
          message: this.$t('label.oauth2.mapping.update.success')
        })
        this.dialogVisible = false
        this.fetchMapping()
      }).catch(error => {
        this.$notification.error({
          message: this.$t('message.request.failed'),
          description: this.errorMessage(error),
          duration: 0
        })
      }).finally(() => {
        this.submitting = false
      })
    },
    handleRemove () {
      this.loading = true
      postAPI('removeOAuth2DomainProviderMapping', {
        domainid: this.resource.id
      }).then(() => {
        this.$notification.success({
          message: this.$t('label.oauth2.mapping.remove.success')
        })
        this.fetchMapping()
      }).catch(error => {
        this.$notification.error({
          message: this.$t('message.request.failed'),
          description: this.errorMessage(error),
          duration: 0
        })
        this.loading = false
      })
    },
    errorMessage (error) {
      return (error?.response?.headers?.['x-description']) || error?.message || ''
    }
  }
}
</script>

<style scoped lang="less">
.empty-state {
  padding: 24px 0;
}
.actions {
  margin-top: 16px;
  display: flex;
  gap: 8px;
}
</style>
